import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as useAPI from 'hooks/useAPI';
import * as api from 'config/api';
import * as env from 'config/environment';
import { GetUserAssetResult, getUserAsset } from 'config/api';
import Loading from 'components/commons/loading/Loading';
import { useRouting } from 'hooks/useRouting';
import { useModal } from 'hooks/useModal';
import { useBrokers } from 'hooks/useBrokers';
import { useHomeUser, isLoading as isMainUserLoading } from 'hooks/useHomeUser';
import { useCustomizeHeaderContext } from 'contexts/CustomizeHeaderContext';
import { useMenuContext } from 'contexts/CustomizeMenuContext';
import { AggregateSourceTypes } from 'config/consts';
import {
  ColumnList,
  ServiceList,
  DividendItem,
} from './assetStuffing/TotalAssetStuffingFluctuationList';
import { GroupAssetTab, TabProps, TotalAssetTab } from './Tab';
import { GroupAdd } from './GroupAdd';
import { HomeContentsInternal } from './HomeContentsInternal';
import { AboutExchangeRateModalButton } from './PortfolioStuffing/asset/PortfolioStuffingAsset';
import { AccountAggregating } from './AccountAggregating';
import { AccountAggregatingCardPicker, CardPicker } from './CardPicker';
import { UpdateDateTimeInternal } from '../UpdateDateTime';
import { AboutExchangeRateModalProvider } from '../modal/AboutExchangeRateModal';
import {
  NeedsMigrateStickyNote,
  WithMigrateToMonoModal,
} from '../modal/MigrateToMonoModal';
import { isIosAppNotPremium } from '../utils/userStatus';

type TotalAssetKey = 0;

export type GroupAssetKey = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;

type AddGroupKey = 9999;

const ASSET_BUNDLE_TYPE = {
  totalAsset: '資産総額',
  groupAsset: 'グループ資産',
  addGroup: 'グループ追加',
} as const;

export type AssetBundleType =
  | {
      type: typeof ASSET_BUNDLE_TYPE['totalAsset'];
      key: TotalAssetKey;
    }
  | {
      type: typeof ASSET_BUNDLE_TYPE['groupAsset'];
      key: GroupAssetKey;
    }
  | {
      type: typeof ASSET_BUNDLE_TYPE['addGroup'];
      key: AddGroupKey;
    };

const isTotalAssetBundle = (
  state: AssetBundleType
): state is {
  type: typeof ASSET_BUNDLE_TYPE['totalAsset'];
  key: TotalAssetKey;
} => state.type === '資産総額';

const STUFFING_TYPE = {
  assets: '資産',
  portfolio: '保有資産',
  profitAndLoss: '損益',
  portfolioRatio: 'ポートフォリオ',
} as const;

export type StuffingType = typeof STUFFING_TYPE[keyof typeof STUFFING_TYPE];

const AGGREGATE_STATE = {
  連携中: 'AGGREGATED',
  未連携: 'NOT_AGGREGATED',
} as const;

type AggregateState = typeof AGGREGATE_STATE[keyof typeof AGGREGATE_STATE];

// メイン

type HomeAccountLinkage = 'stayHome' | 'toAccountLinkageGuidance';

const useHomeAccountLinkageGuidance = (
  brokers: useAPI.State<'state', api.GetUserBrokerResultItem[]>
): HomeAccountLinkage | null => {
  const [
    homeAccountLinkage,
    setHomeAccountLinkage,
  ] = useState<HomeAccountLinkage | null>(null);
  const isShowView = useAPI.isLoaded(brokers) && brokers.state.length > 0;

  useEffect(() => {
    if (useAPI.isLoading(brokers)) return;
    if (isAccountAggregated(brokers.state)) {
      setHomeAccountLinkage('stayHome');
    } else if (brokers.state.length === 0) {
      setHomeAccountLinkage('toAccountLinkageGuidance');
    }
  }, [brokers, isShowView]);
  return homeAccountLinkage;
};

export const isAccountAggregated = (
  brokers: api.GetUserBrokerResultItem[]
): boolean => {
  return brokers.length > 0;
};

export function useSkipAgreeModal() {
  const { brokers, getBrokers } = useBrokers();
  useEffect(() => {
    getBrokers();
  }, [getBrokers]);
  const homeAccountGuidance = useHomeAccountLinkageGuidance(brokers);
  return ((hag: ReturnType<typeof useHomeAccountLinkageGuidance>) => {
    if (hag === null) return 'loading';
    switch (hag) {
      case 'stayHome':
        return 'show';
      case 'toAccountLinkageGuidance':
        return 'later';
    }
  })(homeAccountGuidance);
}

export const isOutOfDateBroker = ({ status }: { status: string | null }) =>
  !['000', null].includes(status);

export const getOutOfDateAccount_ = (
  brokers: useAPI.State<'state', api.GetUserBrokerResultItem[]>
) => (
  arg: { account: string } | { brokerId: string }
): { account: string } | null => {
  if (useAPI.isLoading(brokers)) return null;
  const itemPredicate =
    'account' in arg
      ? (item: api.GetUserBrokerResultItem) => item.Account === arg.account
      : (item: api.GetUserBrokerResultItem) => item.BrokerID === arg.brokerId;
  const found = brokers.state.find(itemPredicate);
  if (!found) return null;
  return isOutOfDateBroker({ status: found.Status })
    ? { account: found.Account }
    : null;
};

type Page =
  | { tag: 'Preview'; element: React.ReactElement }
  | { tag: 'SetRange' };

export const usePreviewGlobalUIItemsEffect = (backHome: () => void) => {
  const {
    customTitle,
    setCustomTitle,
    withBell,
    setWithBell,
    withLogo,
    setWithLogo,
    withNavigation,
    setWithNavigation,
    withBackIconWord,
    setWithBackIconWord,
    setBackFunction,
  } = useCustomizeHeaderContext();
  const { setIsShowMenu, isShowMenu } = useMenuContext();
  const {
    initialCustomTitle,
    initialWithBackIconWord,
    initialWithBell,
    initialWithLogo,
    initialWithNavigation,
    initialIsShowMenu,
  } = useMemo(
    () => ({
      initialCustomTitle: customTitle,
      initialWithBell: withBell,
      initialWithLogo: withLogo,
      initialWithNavigation: withNavigation,
      initialWithBackIconWord: withBackIconWord,
      initialIsShowMenu: isShowMenu,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const effect = () => {
    setCustomTitle('プレビュー');
    setWithLogo(false);
    setWithBell(false);
    setWithNavigation(false);
    setWithBackIconWord(true);
    setIsShowMenu(false);
    const { cleanup: cleanupBackFunction } = setBackFunction(backHome);
    return () => {
      setCustomTitle(initialCustomTitle);
      setWithLogo(initialWithLogo);
      setWithBell(initialWithBell);
      setWithNavigation(initialWithNavigation);
      setWithBackIconWord(initialWithBackIconWord);
      setIsShowMenu(initialIsShowMenu);
      cleanupBackFunction();
    };
  };
  return { effect };
};

export const usePreviewPageGlobalUIItems = (
  page: Page,
  backHome: () => void
) => {
  const { effect } = usePreviewGlobalUIItemsEffect(backHome);
  useEffect(() => {
    switch (page.tag) {
      case 'Preview':
        return effect();
      case 'SetRange':
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page.tag]);
};

export type AccountGroupProps = {
  id: string;
  name: string;
  accounts: Array<string>;
  targetGoalID?: string;
  targetGoalRefType?: number;
  targetGoalTitle?: string;
  insTime: string;
  updTime?: string;
};

const HomeInternal = (): React.ReactElement | null => {
  const { pushRouting } = useRouting();

  const [cardType, setCardType] = useState<AssetBundleType>({
    key: 0,
    type: '資産総額',
  });
  const [stuffingType, setStuffingType] = useState<StuffingType>('資産');
  const { homeUser, getHomeUser, toggleAssetVisibility } = useHomeUser(200);
  const { brokers, getBrokers } = useBrokers();

  const [asset, setAsset] = useState<GetUserAssetResult | null>(null);
  const [accountGroup, setAccountGroup] = useState<AccountGroupProps[] | null>(
    null
  );

  const { setAccountOrAuthenticationError } = useCustomizeHeaderContext();

  useEffect(() => {
    if (
      (homeUser.isAuthenticatedMoneytree === false &&
        homeUser.isLinkedMoneytree) ||
      (useAPI.isLoaded(brokers) &&
        brokers.state.some(item => !['000', null].includes(item.Status)))
    ) {
      setAccountOrAuthenticationError(true);
    } else {
      setAccountOrAuthenticationError(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeUser.isAuthenticatedMoneytree, homeUser.isLinkedMoneytree, brokers]);

  const [page, setPage] = useState<Page>({ tag: 'SetRange' });
  const { setWithLogo, setWithBell } = useCustomizeHeaderContext();

  useEffect(() => {
    const cleanupWithLogo = setWithLogo(true);
    const cleanupWithBell = setWithBell(true);
    return () => {
      cleanupWithLogo();
      cleanupWithBell();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toPreview = useCallback(
    (element: React.ReactElement) => setPage({ tag: 'Preview', element }),
    []
  );

  const backHome = useCallback(() => setPage({ tag: 'SetRange' }), []);
  usePreviewPageGlobalUIItems(page, backHome);

  const addGroup = (): void => {
    // 他のタブにいるときでも、クリックしてしまうと遷移するのでそれを防ぐ対応
    if (cardType.type === 'グループ追加')
      pushRouting({ path: '/grouping' }, true);
  };

  const getTab = useCallback((assetBundleType: AssetBundleType): ((
    props: TabProps
  ) => React.ReactElement) => {
    if (isTotalAssetBundle(assetBundleType)) {
      return TotalAssetTab;
    } else {
      return GroupAssetTab;
    }
  }, []);

  const Tab = useMemo(
    () =>
      cardType.type === 'グループ追加' ? (): null => null : getTab(cardType),
    [cardType, getTab]
  );

  const refetchBrokers = <T,>(_: T): void => {
    getBrokers(cardType.key);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getOutOfDateAccount = useCallback(getOutOfDateAccount_(brokers), [
    brokers,
  ]);

  useEffect(() => {
    const groupID = cardType.key;
    getBrokers(groupID);
    getHomeUser();

    (async () => {
      const res = await getUserAsset(
        groupID !== 0 && groupID !== 9999 ? groupID.toString() : undefined
      );
      setAsset(res);

      const resAccountGroup_ = await api.getAccountGroup();
      const resAccountGroup = await resAccountGroup_.Items.map(item => {
        return {
          id: item.ID,
          name: item.Name,
          accounts: item.Accounts,
          targetGoalID: item.TargetGoalID,
          targetGoalRefType: item.TargetGoalRefType,
          targetGoalTitle: item.TargetGoalTitle,
          insTime: item.InsTime,
          updTime: item.UpdTime,
        };
      });
      await setAccountGroup(resAccountGroup);
    })();

    if (cardType.type === 'グループ資産' && stuffingType === '損益')
      setStuffingType('資産');

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardType]);

  const HomeContents = useMemo(() => {
    if (
      useAPI.isLoading(brokers) ||
      isMainUserLoading(homeUser) ||
      asset === null ||
      accountGroup === null
    )
      return (
        <Loading
          loading={true}
          style={{
            backgroundColor: '#edf2f7',
            alignItems: 'normal',
            paddingTop: '200px',
            position: 'fixed',
            top: '95',
            left: '0',
          }}
        />
      );

    return (
      <>
        <div className="max-w-md mx-auto relative">
          {cardType.type === 'グループ追加' && (
            <div className="bg-white pt-9">
              <GroupAdd addGroup={addGroup} />
            </div>
          )}
          {cardType.type !== 'グループ追加' && (
            <>
              <div className="bg-white pb-5.5">
                <HomeContentsInternal
                  {...{
                    Tab,
                    cardType,
                    setStuffingType,
                    asset,
                    stuffingType,
                    brokers: brokers.state,
                    UserBrokerUpdateTime: (
                      <UpdateDateTimeInternal
                        brokers={brokers.state}
                        updateBrokers={refetchBrokers}
                        textClassName="text-white"
                      />
                    ),
                    isLinkedLine: homeUser.isLinkedLine,
                    getOutOfDateAccount,
                    backHome,
                    toPreview,
                  }}
                />
              </div>
            </>
          )}
        </div>
        {cardType.type !== 'グループ追加' && (
          <>
            <div className="max-w-md mx-auto mt-2 lg:px-0 px-4 bg-gray-200">
              {stuffingType !== '資産' && (
                <div className="flex">
                  <AboutExchangeRateModalButton
                    isShowMessage
                    className="ml-auto right-1"
                  />
                </div>
              )}
              {stuffingType === '保有資産' && (
                <div className="mt-6">
                  <div className="text-bluegray-500 text-xs">
                    ※手入力の資産は「現金・MRF」と「その他」のみ保有資産のリストに表示されます。
                  </div>
                </div>
              )}
              <a
                href={`${env.helpURL}/categories/4405421753369-%E3%81%BF%E3%82%93%E3%81%8B%E3%81%B6%E3%82%A2%E3%82%BB%E3%83%83%E3%83%88%E3%83%97%E3%83%A9%E3%83%B3%E3%83%8A%E3%83%BC`}
                target="_blank"
                rel="noopener noreferrer">
                <div className="flex flex-row items-center mt-8 py-3 px-4 bg-white border border-bluegray-300 rounded-lg">
                  <img src="/static/images/faq.svg" alt="" />
                  <div className="flex flex-col ml-2 font-bold">
                    <div className="text-xs">使い方でお困りですか？</div>
                    <div className="text-sm">
                      アセプラの使い方｜よくある質問へ
                    </div>
                  </div>
                </div>
              </a>
            </div>
          </>
        )}
      </>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardType, addGroup, stuffingType, Tab]);

  if (useAPI.isLoading(brokers))
    return (
      <Loading
        loading={true}
        style={{
          backgroundColor: '#edf2f7',
          alignItems: 'normal',
          paddingTop: '200px',
          position: 'fixed',
          top: '95',
          left: '0',
        }}
      />
    );

  const isOutOfDateBrokers = brokers.state.some(
    item => !['000', '800', null].includes(item.Status)
  );

  switch (page.tag) {
    case 'Preview':
      return page.element;
    case 'SetRange':
      return (
        <>
          {asset === null ||
          accountGroup === null ||
          isMainUserLoading(homeUser) ? (
            <>
              <LoadingCardPicker />
              <LoadingContents />
            </>
          ) : (
            <>
              <div className="bg-slate-800 pb-6">
                <div className="max-w-md mx-auto py-2">
                  <CardPicker
                    {...{
                      setCardType,
                      asset,
                      accountGroup,
                      addGroup,
                      homeUser,
                      brokers: brokers.state,
                      toggleAssetVisibility,
                      isOutOfDateBrokers,
                      refetchBrokers,
                      initialCardType: cardType,
                    }}
                  />
                </div>
              </div>
              {HomeContents}
            </>
          )}
        </>
      );
  }
};

const useAccountLinkageGuidanceRouting = (
  brokers: useAPI.State<'state', api.GetUserBrokerResultItem[]>
): void => {
  const homeAccountLinkage = useHomeAccountLinkageGuidance(brokers);
  const routing = useRouting();

  useEffect(() => {
    if (!homeAccountLinkage) return;
    if (homeAccountLinkage === 'stayHome') {
      routing.pushRouting({ path: '/home' }, true);
    } else if (homeAccountLinkage === 'toAccountLinkageGuidance') {
      routing.pushRouting({ path: '/accountlinkageinfo' }, true);
    }
  }, [routing, homeAccountLinkage]);
};

const getAggregateState = (
  brokers: api.GetUserBrokerResultItem[]
): AggregateState => {
  return brokers.some(b => b.Status !== null) ? 'AGGREGATED' : 'NOT_AGGREGATED';
};

export const Home = (): React.ReactElement | null => {
  const { brokers, getBrokers } = useBrokers();

  useEffect(() => {
    getBrokers();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useAccountLinkageGuidanceRouting(brokers);

  const migrateToMonoModal = useModal();

  if (useAPI.isLoading(brokers))
    return (
      <Loading
        loading={true}
        style={{
          backgroundColor: '#edf2f7',
          alignItems: 'normal',
          paddingTop: '200px',
          position: 'fixed',
          top: '95',
          left: '0',
        }}
      />
    );

  const aggregateState: AggregateState = getAggregateState(brokers.state);

  return (
    <>
      <AboutExchangeRateModalProvider>
        <WithMigrateToMonoModal userBroker={brokers} modal={migrateToMonoModal}>
          {brokers.state.some(
            broker => broker.AggregateSource === AggregateSourceTypes.テコテック
          ) && (
            <div className="max-w-md mx-auto py-2">
              <NeedsMigrateStickyNote
                onClickDetail={migrateToMonoModal[1].showModal}
              />
            </div>
          )}
          {aggregateState === AGGREGATE_STATE.未連携 && (
            <>
              <div className="bg-slate-800 pb-6">
                <div className="max-w-md mx-auto py-2">
                  <AccountAggregatingCardPicker />
                </div>
              </div>
              <AccountAggregating />
            </>
          )}
          {aggregateState === AGGREGATE_STATE.連携中 && <HomeInternal />}
        </WithMigrateToMonoModal>
      </AboutExchangeRateModalProvider>
    </>
  );
};

const LoadingContents = () => {
  return (
    <div className="max-w-md mx-auto relative">
      <div className="bg-white pb-5.5">
        <div className="pt-7 bg-bluegray-600"></div>
        <div className="h-12 absolute left-0 w-full max-w-md -top-6 px-4 z-10">
          <div className="flex h-full rounded-full bg-bluegray-700 px-7">
            <div className="w-full h-full text-xs font-bold px-1 pt-1">
              <div
                id="home_assets"
                className="flex justify-center items-center flex-col flex-1 h-full text-gray-500">
                <i className="icon-area-graph text-gray-500 text-base mb-0.5"></i>
                <div className="leading-5">資産</div>
                <div
                  className="mt-1"
                  style={{ borderRadius: '1px 1px 0px 0px' }}></div>
              </div>
            </div>
            <div className="w-full h-full text-xs font-bold px-1 pt-1">
              <div
                id="home_positions"
                className="flex justify-center items-center flex-col flex-1 h-full text-gray-500">
                <i className="icon-website text-gray-500 text-base mb-0.5"></i>
                <div className="leading-5">保有資産</div>
                <div
                  className="mt-1"
                  style={{ borderRadius: '1px 1px 0px 0px' }}></div>
              </div>
            </div>
            <div className="w-full h-full text-xs font-bold px-1 pt-1">
              <div
                id="home_profitOrLoss"
                className="flex justify-center items-center flex-col flex-1 h-full text-gray-500">
                <i className="icon-timeline text-gray-500 text-base mb-0.5"></i>
                <div className="leading-5">損益</div>
                <div
                  className="mt-1"
                  style={{ borderRadius: '1px 1px 0px 0px' }}></div>
              </div>
            </div>
            <div className="w-full h-full text-xs font-bold px-1 pt-1">
              <div
                id="home_portfolio"
                className="flex justify-center items-center flex-col flex-1 h-full text-gray-500">
                <i className="icon-chart-pie text-gray-500 text-base mb-0.5"></i>
                <div className="leading-5">ﾎﾟｰﾄﾌｫﾘｵ</div>
                <div
                  className="mt-1"
                  style={{ borderRadius: '1px 1px 0px 0px' }}></div>
              </div>
            </div>
          </div>
        </div>
        <div className="mb-4 bg-bluegray-600">
          <Loading
            loading={true}
            circularLoading={true}
            style={{
              paddingTop: '100px',
              paddingBottom: '100px',
            }}
          />
        </div>
        <div className="pt-4 px-4">
          <div className="text-lg font-bold mb-4">資産額の変動</div>
          <Loading
            loading={true}
            circularLoading={true}
            style={{
              paddingTop: '60px',
              paddingBottom: '60px',
            }}
          />
        </div>
        <ServiceList {...{ isTransition: false }} />
        <DividendItem {...{ isTransition: false }} />
        {!isIosAppNotPremium() && (
          <>
            <div className="pt-8 pb-6 px-4">
              <div className="mb-1 text-lg font-bold">
                資産目標 今月の達成状況
              </div>
              <Loading
                loading={true}
                circularLoading={true}
                style={{
                  paddingTop: '60px',
                  paddingBottom: '60px',
                }}
              />
            </div>
          </>
        )}
        <ColumnList />
      </div>
    </div>
  );
};

const LoadingCardPicker = () => {
  return (
    <div className="bg-slate-800 pb-6">
      <div className="max-w-md mx-auto py-2" style={{ maxWidth: '265px' }}>
        <div className="flex flex-col justify-center bg-white rounded-md py-6 px-4 h-full shadow-card">
          <div className="text-center text-sm font-bold leading-5">
            資産総額
          </div>
          <div className="flex justify-center items-center font-bold leading-6">
            <span className="text-xl mr-2">---</span>
            <span className="text-xs">円</span>
          </div>
          <div className="flex items-center text-xs mt-2 pt-1 px-2 border-t border-bluegray-300">
            <div className="text-gray-700 leading-3.5">評価損益</div>
            <div className="flex justify-center items-center ml-auto font-bold leading-6">
              <span className="text-xl mr-2">---</span>
              <span className="text-xs">円</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
